import {
  createComponent,
  EnvironmentInjector,
  inject,
  Injectable,
} from '@angular/core';
import { CircleLayerSpecification, FilterSpecification, LineLayerSpecification, Map, MapGeoJSONFeature, MapLayerMouseEvent, Popup } from 'maplibre-gl';
import { PopupContainerComponent } from '../../../components/popup-container/popup-container.component';
import { KpiSidebarService } from '../../kpi-sidebar.service';

import { Store } from '@ngrx/store';
import { take } from 'rxjs';
import { selectUserLicense } from '../../../store/selectors/licenses/user-license/user-license.selectors';
import { UserLicenseService } from '../../licenses/user-license/user-license.service';

/** The renderer keeps the click handler and highlight within the lifetime of one chat source. */
export interface ChatDistrictPopupHandle {
  clear(): void;
  dispose(): void;
}

@Injectable({
  providedIn: 'root',
})
export class VectorPopupService {
  private environmentInjector = inject(EnvironmentInjector);
  private kpiSidebarService = inject(KpiSidebarService);
  private store = inject(Store);
  private userLicenseService = inject(UserLicenseService);
  private readonly vectorPopupHandlers = new WeakMap<
    Map,
    globalThis.Map<string, (e: MapLayerMouseEvent) => void>
  >();

  constructor() {}

  releasePopups(map: Map): void {
    this.vectorPopupHandlers
      .get(map)
      ?.forEach((handler, layerId) => map.off('click', layerId, handler));
    this.vectorPopupHandlers.delete(map);
  }

  addPopup(map: Map, layerId: string, popupType: string = 'default') {
    let handlersByLayer = this.vectorPopupHandlers.get(map);
    if (!handlersByLayer) {
      handlersByLayer = new globalThis.Map();
      this.vectorPopupHandlers.set(map, handlersByLayer);
    }
    const previousHandler = handlersByLayer.get(layerId);
    if (previousHandler) {
      map.off('click', layerId, previousHandler);
    }

    const onClick = (e: MapLayerMouseEvent) => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: [layerId],
      });

      if (!features?.length) return;

      const feature = features[0];
      const properties = feature.properties;
      const districtName =
        properties?.['name_eng'] ||
        properties?.['name'] ||
        properties?.['wijk_eng'] ||
        '';

      const layerInfo = map.getLayer(layerId);
      if (!layerInfo) return;

      const sourceId = layerInfo.source;
      const sourceLayer = layerInfo.sourceLayer;

      const filterProps = this.featureSelectionFilters(feature);

      if (filterProps.length === 0) {
        console.warn(
          'Impossibile evidenziare: la feature non ha un ID o proprietà su cui filtrare.',
        );
      } else {
        const lineLayerId = 'vector-highlight-line';
        const circleLayerId = 'vector-highlight-circle';

        const existingLine = map.getLayer(lineLayerId);
        if (
          existingLine &&
          (existingLine.source !== sourceId ||
            existingLine.sourceLayer !== sourceLayer)
        ) {
          map.removeLayer(lineLayerId);
          if (map.getLayer(circleLayerId)) {
            map.removeLayer(circleLayerId);
          }
        }

        if (!map.getLayer(lineLayerId)) {
          const layerConfig: LineLayerSpecification = {
            id: lineLayerId,
            type: 'line',
            source: sourceId,
            filter: ['all', ...filterProps, ['!=', ['geometry-type'], 'Point']] as FilterSpecification,
            paint: {
              'line-color': '#00FFFF',
              'line-width': 3,
            },
          };
          if (sourceLayer) layerConfig['source-layer'] = sourceLayer;
          map.addLayer(layerConfig);
        } else {
          map.setFilter(lineLayerId, [
            'all',
            ...filterProps,
            ['!=', ['geometry-type'], 'Point'],
          ] as FilterSpecification);
        }

        if (!map.getLayer(circleLayerId)) {
          const layerConfig: CircleLayerSpecification = {
            id: circleLayerId,
            type: 'circle',
            source: sourceId,
            filter: ['all', ...filterProps, ['==', ['geometry-type'], 'Point']] as FilterSpecification,
            paint: {
              'circle-color': '#00FFFF',
              'circle-radius': 8,
              'circle-stroke-width': 2,
              'circle-stroke-color': '#FFFFFF',
            },
          };
          if (sourceLayer) layerConfig['source-layer'] = sourceLayer;
          map.addLayer(layerConfig);
        } else {
          map.setFilter(circleLayerId, [
            'all',
            ...filterProps,
            ['==', ['geometry-type'], 'Point'],
          ] as FilterSpecification);
        }
      }
      const coordinates = e.lngLat;

      const typeValue = properties?.['popup_type'];
      const resolvedPopupType = this.resolvePopupType(typeValue);
      const popupData = this.extractPopupData(properties?.['popup_data']);

      // Check license before showing KPI component
      this.store
        .select(selectUserLicense)
        .pipe(take(1))
        .subscribe((userLicense) => {
          const hasLicense = this.userLicenseService.getAvailableTool(
            userLicense?.tools,
            'kpi',
          );
          if (!userLicense || hasLicense) {
            this.kpiSidebarService.showKPIComponent(
              resolvedPopupType,
              { popup_data: popupData, districtName: districtName },
              'vector',
            );
          }
        });
    };

    map.on('click', layerId, onClick);
    handlersByLayer.set(layerId, onClick);
  }
  /** Bind one temporary district overlay to the existing KPI sidebar. */
  addKpiDistrictSelection(
    map: Map,
    layerId: string,
    rasterLayerUuid: string,
    popupType: string,
    zonePropertyName: string,
  ): ChatDistrictPopupHandle {
    const highlightId = `${layerId}-selection`;
    const previousCursor = map.getCanvas().style.cursor;

    const clear = (): void => {
      if (map.getLayer(highlightId)) map.removeLayer(highlightId);
    };
    const onClick = (event: MapLayerMouseEvent): void => {
      const feature = map.queryRenderedFeatures(event.point, { layers: [layerId] })[0];
      if (!feature) return;
      const layer = map.getLayer(layerId);
      if (!layer) return;

      const rawData = feature.properties?.['popup_data'];
      if (rawData === undefined || rawData === null) return;

      const zoneValue = feature.properties?.[zonePropertyName];
      const filter: FilterSpecification[] =
        zoneValue !== null && zoneValue !== undefined
          ? [['==', ['get', zonePropertyName], zoneValue]]
          : this.featureSelectionFilters(feature);
      clear();
      if (filter.length) {
        const highlight: LineLayerSpecification = {
          id: highlightId,
          type: 'line',
          source: layer.source,
          filter: ['all', ...filter] as FilterSpecification,
          paint: { 'line-color': '#00FFFF', 'line-width': 3 },
        };
        if (layer.sourceLayer) highlight['source-layer'] = layer.sourceLayer;
        map.addLayer(highlight);
      }

      const properties = feature.properties ?? {};
      const districtName = String(
        properties['name'] ?? properties['name_eng'] ??
        properties['district'] ?? properties['zone_name'] ?? zoneValue ?? '',
      );
      const actualPopupType = this.resolvePopupType(properties['popup_type']) ?? popupType;
      const popupData = this.extractPopupData(rawData);
      this.store.select(selectUserLicense).pipe(take(1)).subscribe((userLicense) => {
        const hasLicense = this.userLicenseService.getAvailableTool(userLicense?.tools, 'kpi');
        if (!userLicense || hasLicense) {
          this.kpiSidebarService.showKPIComponent(
            actualPopupType,
            { popup_data: popupData, districtName },
            'vector',
            rasterLayerUuid,
          );
        }
      });
    };
    const onEnter = (): void => { map.getCanvas().style.cursor = 'pointer'; };
    const onLeave = (): void => { map.getCanvas().style.cursor = previousCursor; };
    map.on('click', layerId, onClick);
    map.on('mouseenter', layerId, onEnter);
    map.on('mouseleave', layerId, onLeave);
    return {
      clear,
      dispose: () => {
        map.off('click', layerId, onClick);
        map.off('mouseenter', layerId, onEnter);
        map.off('mouseleave', layerId, onLeave);
        map.getCanvas().style.cursor = previousCursor;
        clear();
      },
    };
  }

  addChatDistrictPopup(map: Map, layerId: string, layerName: string): ChatDistrictPopupHandle {
    const highlightId = `${layerId}-district-highlight`;
    let popup: Popup | null = null;
    const clear = (): void => {
      popup?.remove();
      popup = null;
      if (map.getLayer(highlightId)) map.removeLayer(highlightId);
    };

    const onClick = (event: MapLayerMouseEvent): void => {
      const feature = map.queryRenderedFeatures(event.point, { layers: [layerId] })[0];
      if (!feature) return;
      const layerInfo = map.getLayer(layerId);
      if (!layerInfo) return;
      const filters = this.featureSelectionFilters(feature);
      clear();
      if (filters.length) {
        const highlightLayer: LineLayerSpecification = {
          id: highlightId,
          type: 'line',
          source: layerInfo.source,
          filter: ['all', ...filters, ['!=', ['geometry-type'], 'Point']] as FilterSpecification,
          paint: { 'line-color': '#00FFFF', 'line-width': 3 },
        };
        if (layerInfo.sourceLayer) highlightLayer['source-layer'] = layerInfo.sourceLayer;
        map.addLayer(highlightLayer);
      }
      popup = new Popup({ closeButton: true, maxWidth: '320px' })
        .setLngLat(event.lngLat)
        .setDOMContent(this.chatDistrictContent(layerName, feature.properties ?? {}))
        .addTo(map);
    };
    map.on('click', layerId, onClick);
    return {
      clear,
      dispose: () => {
        map.off('click', layerId, onClick);
        clear();
      },
    };
  }

  private featureSelectionFilters(feature: MapGeoJSONFeature): FilterSpecification[] {
    if (feature.id !== undefined && feature.id !== null) {
      return [['==', ['id'], feature.id]];
    }
    // Stable district name works across tile boundaries (clipped MVT features).
    for (const key of ['name_eng', 'name', 'wijk_eng', 'district', 'zone_name']) {
      const value = feature.properties?.[key];
      if (typeof value === 'string' && value.trim()) {
        return [['==', ['get', key], value]];
      }
    }
    const filters: FilterSpecification[] = [];
    for (const [key, value] of Object.entries(feature.properties ?? {})) {
      if (value !== null && (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')) {
        filters.push(['==', ['get', key], value]);
      }
    }
    return filters;
  }

  private chatDistrictContent(layerName: string, properties: Record<string, unknown>): HTMLElement {
    const container = document.createElement('div');
    const name = properties['name_eng'] ?? properties['name'] ?? properties['wijk_eng']
      ?? properties['district'] ?? properties['zone_name'] ?? layerName;
    const title = document.createElement('strong');
    title.textContent = String(name);
    container.appendChild(title);

    const table = document.createElement('table');
    const entries = Object.entries(properties).filter(([key, value]) =>
      value !== null && value !== undefined && !['geometry', 'popup_data'].includes(key),
    ).slice(0, 12);
    for (const [key, value] of entries) {
      const row = document.createElement('tr');
      const label = document.createElement('th');
      label.textContent = key.replace(/_/g, ' ');
      label.style.cssText = 'text-align:left;padding:3px 10px 3px 0;vertical-align:top;';
      const cell = document.createElement('td');
      const raw = typeof value === 'object' ? JSON.stringify(value) : String(value);
      cell.textContent = raw.length > 180 ? `${raw.slice(0, 177)}...` : raw;
      cell.style.cssText = 'padding:3px 0;overflow-wrap:anywhere;';
      row.append(label, cell);
      table.appendChild(row);
    }
    if (!entries.length) {
      const empty = document.createElement('div');
      empty.textContent = 'No district properties available';
      container.appendChild(empty);
    } else {
      container.appendChild(table);
    }
    return container;
  }

  private resolvePopupType(typeValue: unknown): string | null {
    if (typeof typeValue === 'string' && typeValue.trim() !== '') {
      return typeValue;
    }
    if (typeof typeValue === 'number' && typeValue > 0) {
      return typeValue.toString();
    }
    return null;
  }
  private extractPopupData(rawPopupData: unknown): unknown {
    try {
      if (Array.isArray(rawPopupData)) {
        return rawPopupData
          .map((entry) => {
            try {
              return typeof entry === 'string' ? JSON.parse(entry) : entry;
            } catch {
              return null;
            }
          })
          .filter(
            (d): d is Record<string, unknown> => !!d && typeof d === 'object',
          );
      }

      if (typeof rawPopupData === 'string') {
        const firstParse = JSON.parse(rawPopupData);

        if (Array.isArray(firstParse) && typeof firstParse[0] === 'string') {
          return firstParse
            .map((entry) => {
              try {
                return JSON.parse(entry);
              } catch {
                return null;
              }
            })
            .filter(
              (d): d is Record<string, unknown> => !!d && typeof d === 'object',
            );
        }

        return firstParse?.data ?? firstParse;
      }

      return (rawPopupData as { data?: unknown } | null)?.data ?? rawPopupData;
    } catch {
      return null;
    }
  }

  getDefaultPopup(properties: { [key: string]: unknown }): string {
    const columns = Object.keys(properties).map((key) => ({
      key,
      label: key.charAt(0).toUpperCase() + key.slice(1),
    }));

    return this.formatTable(columns, [properties]);
  }

  formatTable(
    columns: { key: string; label: string }[],
    data: { [key: string]: unknown }[],
  ): string {
    if (data.length === 0) {
      return `<div class="popup-content">No data available</div>`;
    }

    let table = `
      <table class="table table-striped table-bordered">
        <thead class="thead-dark">
          <tr>
            ${columns.map((col) => `<th>${col.label}</th>`).join('')}
          </tr>
        </thead>
        <tbody>
          ${data
            .map(
              (row) => `
            <tr>
              ${columns.map((col) => `<td>${String(row[col.key] ?? '')}</td>`).join('')}
            </tr>`,
            )
            .join('')}
        </tbody>
      </table>`;

    return `
    <div class="popup-content" style="overflow-x: auto; max-width: 100%; min-height: 100px; max-height: 250px;">
      ${table}
    </div>`;
  }
}
